import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { getAccessToken } from "../../../utils/authStorage";
import MobileGameCard from "./MobileGameCard";
import styles from "./MobileHome.module.css";
import { bannerAPI } from "../../utils/api";
import BlogMiniSection from "./BlogMiniSection";
import { QuickPitch } from "@/components/home/Reseller/QuickPitch";
import TrustPilot from "@/components/trustpilot/TrustPilot";
import SubscriptionSection from "@/components/subscription/SubscriptionSection";
import {
  Crown,
  Gift,
  Handshake,
  LaptopMinimal,
  Medal,
  PackageOpen,
  ShoppingCart,
  Smartphone,
  Trophy,
  Users,
} from "lucide-react";
import MobileIcon from "./MobileIcon";

const MobileHome = () => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const [games, setGames] = useState([]);
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    fetchGames();
    fetchBanners();
  }, [isAuthenticated]);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [banners.length, currentBannerIndex]);

  const fetchBanners = async () => {
    try {
      const data = await bannerAPI.getPublicBanners();
      setBanners(data);
    } catch (err) {
      console.error("Error fetching banners:", err);
      setBanners([
        {
          _id: "1",
          imageUrl:
            "https://digitaltopup.in/banners/1745071699852--photo-output.jpeg",
          altText: "Promo Banner",
        },
      ]);
    }
  };

  const fetchGames = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/games?limit=12`,
      );
      const data = await response.json();
      if (data.success) {
        setGames(data.games);
      }
    } catch (error) {
      console.error("Error fetching games:", error);
    }
  };

  const quickActions = [
    {
      icon: <ShoppingCart />,
      label: "Orders",
      path: "/user-dashboard/orders",
      color: "#eab308", // Amber
    },
    {
      icon: <Medal />,
      label: "Ranking",
      path: "/leaderboard",
      color: "#f59e42", // Orange
    },
    {
      icon: <Trophy />,
      label: "Tournaments",
      path: "/tournament",
      color: "#3b82f6", // Blue
    },
    {
      icon: <Crown />,
      label: "VIP Tiers",
      path: "/vip-tiers",
      color: "#8b5cf6", // Violet
    },
  ];

  const links = [
    {
      icon: <Trophy />,
      label: "Tournament",
      color: "#eab308",
      path: "/tournament",
    },
    {
      icon: <Crown />,
      label: "VIP",
      color: "#f59e42",
      path: "/vip-tiers",
    },
    {
      icon: <Handshake />,
      label: "Reseller",
      path: "/retail",
      color: "#22c55e",
    },
    {
      icon: <PackageOpen />,
      label: "Mystery Box",
      color: "#0ea5e9",
      path: "/mystery-box",
    },
    {
      icon: <Users />,
      label: "About Us",
      color: "#3B82F6",
      path: "/tournament/about",
    },
  ];

  return (
    <div
      className={`${styles.mobileHome} ${theme === "dark" ? styles.dark : ""}`}
    >
      {/* Hero Section */}
      <div className={styles.heroSection}>

        {/* Banner */}
        <div className={styles.bannerContainer}>
          {banners.map((banner, index) => (
            <div
              key={banner._id}
              className={`${styles.banner} ${index === currentBannerIndex ? styles.active : ""}`}
              onClick={() =>
                banner.link && (window.location.href = banner.link)
              }
              style={{ cursor: banner.link ? "pointer" : "default" }}
            >
              <img src={banner.imageUrl} alt={banner.altText} />
              <div className={styles.bannerTitle}><h2>{banner.title || "NEOBEAST UNLEASHED"}</h2></div>
              <div className={styles.bannerOverlay} />
            </div>
          ))}

          {banners.length > 1 && (
            <div className={styles.indicators}>
              {banners.map((_, i) => (
                <div
                  key={i}
                  className={`${styles.dotContainer} ${i === currentBannerIndex ? styles.active : ''}`}
                  onClick={() => setCurrentBannerIndex(i)}
                >
                  <div className={styles.dotBase} />
                  {/* The filling bar is only visible/animating when active */}
                  <div className={styles.dotFill} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}

      </div>

      <div className={styles.quickActions}>
        {quickActions.map((action, index) => (
          <Link key={index} to={action.path} className={styles.actionButton}>
            <MobileIcon link={action} />
          </Link>
        ))}
      </div>

      <div className={styles.categoriesSection}>
        <h2 className={styles.sectionTitle}>Explore More</h2>
        <div className={styles.categoriesScroll}>
          {links.map((category, index) => (
            <Link to={category.path} key={index}>
              <MobileIcon link={category} />
            </Link>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className={styles.categoriesSection}>
        <h2 className={styles.sectionTitle}>Categories</h2>
        <div className={styles.categoriesScroll}>
          {[
            { label: "Mobile Games", icon: <Smartphone />, color: "#34c759" },
            { label: "Pc Games", icon: <LaptopMinimal />, color: "#0ea5e9" },
            { label: "Game Vouchers", icon: <Gift />, color: "#fbbf24" },
          ].map((category, index) => (
            <div
              key={index}
              className={`${styles.categoryCard} ${selectedCategory === category.label ? styles.active : ""}`}
              onClick={() =>
                setSelectedCategory(
                  selectedCategory === category.label ? "" : category.label,
                )
              }
            >
              <MobileIcon link={category} />
            </div>
          ))}
        </div>
      </div>

      {/* Popular Games - Horizontal Scroll */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {selectedCategory ? `${selectedCategory}` : "Popular Games"}
        </h2>
        <div className={styles.horizontalScroll}>
          {games
            .filter(
              (game) => !selectedCategory || game.category === selectedCategory,
            )
            .slice(0, 6)
            .map((game) => (
              <MobileGameCard key={game._id} game={game} variant="horizontal" />
            ))}
        </div>
      </div>

      {/* All Games - Grid */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {selectedCategory ? `All ${selectedCategory}` : "All Games"}
        </h2>
        <div className={styles.gamesGrid}>
          {games
            .filter(
              (game) => !selectedCategory || game.category === selectedCategory,
            )
            .map((game) => (
              <MobileGameCard key={game._id} game={game} variant="" />
            ))}
        </div>
      </div>

      <div className={styles.blogSection}>
        <h2 className={styles.sectionTitle}>Blogs & More</h2>
        <BlogMiniSection />
        <div className="text-center">
          <Link to="/blogs" className={styles.viewAllButton}>
            View All
            <span>→</span>
          </Link>
        </div>
      </div>

      <div className="flex justify-center px-[1rem] mb-4 ">
        <QuickPitch />
      </div>

      <div className="flex justify-center px-[1rem] mb-4 ">
        <SubscriptionSection />
      </div>

      {/* Trustpilot Section */}
      <TrustPilot />


    </div>
  );
};

export default MobileHome;
